import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchNotifications, readUnreadCount } from "../api/notificationApi";
import {
  NOTIFICATIONS_QUERY_KEY,
  NOTIFICATION_BUCKET,
  NOTIFICATION_GC_TIME,
  NOTIFICATION_PAGE_SIZE,
  NOTIFICATION_STALE_TIME,
} from "../constants";
import { isUnread, toNotificationList } from "../model/notificationModel";
import { buildNotificationSections } from "../model/notificationSections";

export function useNotifications({ enabled = true } = {}) {
  const query = useInfiniteQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: ({ pageParam = 1 }) =>
      fetchNotifications({ page: pageParam, perPage: NOTIFICATION_PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: NOTIFICATION_STALE_TIME,
    gcTime: NOTIFICATION_GC_TIME,
    enabled,
  });

  const pages = query.data?.pages ?? [];

  const notifications = useMemo(() => {
    const loadedPages = query.data?.pages ?? [];

    return [
      ...loadedPages.flatMap((page) =>
        toNotificationList(page.unreadItems, NOTIFICATION_BUCKET.UNREAD),
      ),
      ...loadedPages.flatMap((page) =>
        toNotificationList(page.readItems, NOTIFICATION_BUCKET.READ),
      ),
    ];
  }, [query.data]);

  const sections = useMemo(() => buildNotificationSections(notifications), [notifications]);

  const pagination = pages[pages.length - 1]?.pagination ?? pages[0]?.pagination ?? {};

  const serverUnreadCount = readUnreadCount(pagination);

  const loadedUnreadCount = useMemo(() => notifications.filter(isUnread).length, [notifications]);

  return {
    sections,
    notifications,
    totalCount: pagination.totalCount ?? notifications.length,
    unreadCount: Number.isFinite(serverUnreadCount) ? serverUnreadCount : loadedUnreadCount,
    isEmpty: !query.isLoading && notifications.length === 0,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
}
